/* =============================================================================
 * Mifold — dw-events-core.js  (TAU-357 / TAU-360, Mifold Test 1)
 * Taurist Technologies Inc.
 *
 * PURE logic for snippets/dw-events.liquid. No DOM queries, no network, no
 * globals reached for — every input is a parameter, so `node --test qa/*.test.mjs`
 * can exercise it with plain objects (see qa/dw-events.test.mjs).
 *
 * Loaded by the snippet via the theme's asset_url | script_tag (parser-blocking,
 * so it is defined before the snippet's inline script runs) and exposed as
 * window.__dwEventsCore. Also require()-able in Node.
 * ========================================================================== */
(function () {
  'use strict';

  var EXPERIMENT_NAME_KEY = 'mifold_mobile_pdp_t1'; /* frozen — plan Global Constraints */
  var ATTEMPT_MAX_AGE_MS = 10000;                   /* brief §2: correlate a click <=10s old */
  var CLICK_DEDUPE_MS = 300;                        /* brief §3 */

  /* --- uuid v4, crypto.randomUUID with a Math.random fallback (Global
         Constraints). Kept here so the snippet stays thin and there is one
         implementation for every event_id / attempt_id. --------------------- */
  function uuid4() {
    try {
      if (typeof crypto !== 'undefined' && crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) { /* fall through */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /* --- URL matching -------------------------------------------------------
     isAddUrl must match ONLY the add endpoint: /cart/update.js (used by the
     cart-attribute writer below and by the theme's drawer quantity stepper)
     must never be mistaken for an add, or the attribute write would re-enter
     the ATC logic (brief §8 loop guard). Locale-prefixed URLs (/en/cart/add.js)
     and absolute URLs both match. ---------------------------------------- */
  function isAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url == null ? '' : url).split('#')[0]);
  }

  /* The theme refreshes the drawer with fetch('/cart.js') and
     fetch('/?section_id=cart-drawer') — see sections/cart-drawer.liquid and
     layout/theme.liquid's updateCartDrawer(). Both are the "cart refresh"
     whose failure the brief wants reported as cart_drawer_error (§4). */
  function isCartRefreshUrl(url) {
    var u = String(url == null ? '' : url);
    return /\/cart\.js(\?|$)/.test(u.split('#')[0]) || /section_id=cart-drawer/.test(u);
  }

  /* --- ATC click bookkeeping ---------------------------------------------
     recordClick returns null for a repeat click on the SAME target inside
     CLICK_DEDUPE_MS (brief §3) so the caller can simply skip the push.
     takeAttempt does NOT consume the attempt: one click can legitimately
     produce add_to_cart AND a later cart_drawer_error under one attempt_id. */
  function recordClick(state, target, now, id) {
    var last = state.last;
    if (last && last.target === target && (now - last.at) < CLICK_DEDUPE_MS) return null;
    state.last = { id: id, target: target, at: now };
    return state.last;
  }

  function takeAttempt(state, now) {
    var last = state.last;
    if (!last) return null;
    return (now - last.at) <= ATTEMPT_MAX_AGE_MS ? last : null;
  }

  /* --- CTA location ------------------------------------------------------- */
  function locationOf(el) {
    try {
      if (el && el.closest && el.closest('[class*="sticky"], [data-sticky-atc]')) return 'sticky';
    } catch (e) { /* exotic selector support */ }
    return 'primary';
  }

  /* --- error categorisation ----------------------------------------------
     Shopify's /cart/add.js failure body is { status, message, description }.
     The description is matched BEFORE the status so a 422 can resolve to the
     specific cause; anything unmatched degrades to http_<status>. ---------- */
  function errorCategory(status, description) {
    var d = String(description == null ? '' : description);
    if (/cannot find variant|invalid variant|unavailable|variant .*(does not|doesn't) exist/i.test(d)) return 'invalid_variant';
    if (/sold out|out of stock|are in your cart|no longer available|not enough|only .*(is|are) available/i.test(d)) return 'sold_out';
    if (status === 404) return 'invalid_variant';
    if (status === 0 || status === null || status === undefined) return 'network';
    if (typeof status !== 'number' || !isFinite(status)) return 'unknown';
    if (status >= 200 && status < 300) return 'unknown';
    return 'http_' + status;
  }

  /* --- /cart/add.js response ---------------------------------------------
     Returns the added line item(s). Shopify answers a single-line add with a
     bare line-item object and a multi-line add with an items array; both
     shapes are handled. Prices are cents. Ids are stringified per the plan's
     dataLayer contract (large Shopify ids must never round-trip a JS Number). */
  function itemsFromAddResponse(json) {
    if (!json || typeof json !== 'object') return null;
    var lines = Array.isArray(json.items) ? json.items : [json];
    var items = [];
    var quantity = 0;
    var cents = 0;
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      if (!l || l.variant_id === null || l.variant_id === undefined) continue;
      var q = typeof l.quantity === 'number' ? l.quantity : 1;
      var p = typeof l.price === 'number' ? l.price : 0;
      quantity += q;
      cents += p * q;
      items.push({
        item_id: String(l.variant_id),
        item_name: l.product_title || l.title || undefined,
        quantity: q,
        price: p / 100
      });
    }
    if (!items.length) return null;
    var first = lines[0] || {};
    return {
      items: items,
      quantity: quantity,
      value: cents / 100,
      product_id: first.product_id === null || first.product_id === undefined ? null : String(first.product_id),
      product_handle: first.handle || null,
      variant_id: String(first.variant_id)
    };
  }

  /* --- cart -> GA4 ecommerce ----------------------------------------------
     One shape in, one shape out: the snippet seeds `cart` from Liquid in the
     /cart.js wire format, then overwrites it verbatim with any /cart.js
     response it observes, so begin_checkout never reads a stale server render. */
  function cartToEcommerce(cart, currency) {
    var lines = (cart && Array.isArray(cart.items)) ? cart.items : [];
    var items = [];
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      if (!l || l.variant_id === null || l.variant_id === undefined) continue;
      items.push({
        item_id: String(l.variant_id),
        item_name: l.product_title || l.title || undefined,
        quantity: typeof l.quantity === 'number' ? l.quantity : 1,
        price: (typeof l.price === 'number' ? l.price : 0) / 100
      });
    }
    return {
      currency: currency || undefined,
      value: (cart && typeof cart.total_price === 'number') ? cart.total_price / 100 : 0,
      items: items
    };
  }

  /* --- cart attributes (TAU-360) ------------------------------------------
     dw_exp_ts is deliberately EXCLUDED from attributesDiffer: it changes on
     every page load, so including it would make every load "different" and
     fire a /cart/update.js write forever. dw_vid + dw_exp are the identity. */
  function cartAttributes(vid, arm, tsIso) {
    return {
      dw_vid: vid,
      dw_exp: EXPERIMENT_NAME_KEY + ':' + arm,
      dw_exp_ts: tsIso
    };
  }

  function attributesDiffer(current, desired) {
    var c = current || {};
    return c.dw_vid !== desired.dw_vid || c.dw_exp !== desired.dw_exp;
  }

  /* --- variant selection --------------------------------------------------
     Returns null (no push) when there is no previous id to diff against or
     when the id did not actually change — the "do NOT fire on initial
     selection" rule in brief §6, belt-and-braces on top of only ever binding
     to 'change'. State is seeded from Liquid's selected_or_first_available. */
  function variantSelect(state, newVariantId, option) {
    var next = (newVariantId === null || newVariantId === undefined || newVariantId === '') ? null : String(newVariantId);
    var prev = (state.variantId === null || state.variantId === undefined || state.variantId === '') ? null : String(state.variantId);
    if (!next) return null;
    if (!prev || prev === next) { state.variantId = next; return null; }
    state.variantId = next;
    return { prev_variant_id: prev, new_variant_id: next, option: option || null };
  }

  /* --- media identity / gallery resolution (TAU-358) -----------------------
     brief section 3 "Image measurement": media_id is the Shopify media id when
     the markup carries one, else the image's src PATH with the size params
     stripped. Dropping the origin and the ?v= cache buster means the same
     asset served at 1200x on the slide and 400x in a thumbnail (or over a
     protocol-relative url) resolves to ONE id — which is what makes "shared
     images retain the same media ID across colour changes" true. ---------- */
  function mediaIdFromSrc(src) {
    var u = String(src == null ? '' : src).split('#')[0].split('?')[0];
    u = u.replace(/^(?:https?:)?\/\/[^/]+/, '');
    /* Shopify size suffixes: _1200x, _600x800, _x800, plus @2x and _crop_center */
    u = u.replace(/_(?:\d+x\d*|x\d+)(?:@\d+x)?(?:_crop_[a-z]+)?(?=\.[a-z0-9]+$)/i, '');
    return u || null;
  }

  function attrOf(el, name) {
    try { return (el && el.getAttribute) ? el.getAttribute(name) : null; } catch (e) { return null; }
  }
  function qs(el, sel) {
    try { return (el && el.querySelector) ? el.querySelector(sel) : null; } catch (e) { return null; }
  }

  /* firstMatch is how "an empty widget does not qualify" is enforced: the
     reviews wiring observes the CARD this returns, never the container, so a
     container that has not rendered a card yet is simply not observed. */
  function firstMatch(root, sel) {
    return qs(root, sel);
  }

  /* One gallery slide -> { el, media_id, media_type }. `idAttrs` is the
     selector map's list, tried on the slide first and then on the media
     element (Turbo puts data-image-id on the <img>, other themes put a
     data-media-id on the slide). Video wins over image: a slide holding an
     <iframe> or <video> is video even if it also holds a poster <img>. */
  function describeMedia(item, idAttrs) {
    var v = qs(item, 'video') || qs(item, 'iframe');
    var media = v || qs(item, 'img');
    var attrs = idAttrs || ['data-media-id'];
    var id = null;
    for (var i = 0; i < attrs.length && !id; i++) {
      id = attrOf(item, attrs[i]) || attrOf(media, attrs[i]);
    }
    /* An HTML5 <video> usually carries no src of its own — the treatment
       theme's is <video poster=…><source src=…mp4></video> — so fall through
       to the first <source> and finally to the poster. Without this the
       treatment's video slide has no id at all and is skipped entirely: no
       pdp_media_view and no pdp_video_progress for the one slide the
       treatment added. */
    if (!id) {
      id = mediaIdFromSrc(
        attrOf(media, 'src') ||
        attrOf(media, 'data-src') ||
        attrOf(qs(media, 'source'), 'src') ||
        attrOf(media, 'poster')
      );
    }
    return { el: media, media_id: id || null, media_type: v ? 'video' : 'image' };
  }

  /* --- selector map (TAU-365) ---------------------------------------------
     ONE dw-events.liquid serves both themes: control (branch main, theme
     177343955237) and treatment (branch sep26-pdp-test-taurist, theme
     191346049317). The arm is resolved from the MARKUP, never from a branch
     or a hardcoded theme id — sections/product-template.liquid:107 puts
     .mifold-comfort-treatment-host / .hifold-treatment-host on the product
     section, and only the treatment theme has it.

     It is the THEME that is keyed, not the active layout: the host class is
     present at every width while the treatment only activates itself at
     <=798px. That is the right key, and it is why the treatment ATC selectors
     are a SUPERSET of the control ones — an unenrolled >798px visitor on the
     treatment theme is looking at the control markup.

     Verified against the treatment markup on 09 Sep 2026:
       .mct__submit / .hft__submit — created in JS at
           snippets/mifold-comfort-mobile-treatment.liquid:723 and
           snippets/hifold-mobile-treatment.liquid:717, type="button", so the
           control's form[…] button[type=submit] rule cannot reach them; the
           theme's own .add_to_cart is set hidden while the treatment is
           active (mct :879 / hft :878, CSS mct :557), so observing it alone
           would report nothing.
       .mct__sticky-submit / .hft__sticky-submit — mct :727 / hft :721.
           position:fixed (CSS mct :671), toggled by .is-visible through
           visibility + transform (mct :696), NOT display:none, so an
           IntersectionObserver reports them honestly. The class contains
           "sticky", so locationOf() resolves them to 'sticky' with no extra
           rule; the bar itself also carries data-sticky-atc (mct :134).
       [data-mct-gallery-track] / [data-hft-gallery-track] — mct :49 / hft :50.
           Scoping to the TRACK excludes the dots strip; the four <template>
           galleries are unreachable from document.querySelectorAll by
           definition. The track's children are wholesale replaced on colour
           change (mct :791 galleryTrack.replaceChildren) — the snippet's
           MutationObserver on the track's parent already covers that.
       [data-mct-slide] / [data-hft-slide] — snippets/mifold-comfort-gallery-
           items.liquid:46 and snippets/hifold-gallery-items.liquid:42.
       idAttrs — the treatment slides carry NO id attribute of any kind, so
           every media_id comes from the asset path via mediaIdFromSrc (and the
           <source>/poster fallback above, mct gallery-items :48-56, is what
           makes the video slide count at all).
       reviews — UNCHANGED: the treatment keeps the Opinew widget and links to
           it with <a href="#opinew-plugin"> (mct :85 / hft :83), already
           matched by the control REVIEWS.link. The Opinew widget itself is an
           app block in a SEPARATE top-level section (templates/product.json
           :1113-1122), not inside the product section the treatment hides, so
           REVIEWS.container / .card are unaffected.
           ---------------------------------------------------------------- */
  var TREATMENT_HOST_SEL = '.mifold-comfort-treatment-host, .hifold-treatment-host';
  var TREATMENT_CTA_SEL = '.mct__submit, .mct__sticky-submit, .hft__submit, .hft__sticky-submit';

  var CONTROL_MAP = {
    arm: 'control',
    gallery: {
      root: '.js-product-page-gallery .js-product-gallery',
      item: '.gallery-cell',
      idAttrs: ['data-media-id', 'data-image-id', 'data-video-id']
    },
    reviews: {
      link: 'a.opinew-stars-plugin-link, a[href*="#opinew"], a[href*="#reviews"], a[href*="reviews"], .opinew-reviews-link, [data-reviews-link]',
      container: '#opinew-plugin, .opw-reviews-container, [data-opinew-reviews]',
      card: '.opw-review-content, .opinew-review, [data-opinew-review]'
    },
    atc: 'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], [data-add-to-cart], .product-form__submit, .ajax-add-to-cart, .buy_now_button',
    atcVisible: 'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], [class*="sticky"] button[type="submit"], [class*="sticky"] [data-add-to-cart], [class*="sticky"] .product-form__submit, [data-sticky-atc] button[type="submit"], [data-sticky-atc] [data-add-to-cart], [data-sticky-atc] .product-form__submit'
  };

  var TREATMENT_MAP = {
    arm: 'treatment',
    gallery: {
      root: '[data-mct-gallery-track], [data-hft-gallery-track]',
      item: '[data-mct-slide], [data-hft-slide]',
      idAttrs: CONTROL_MAP.gallery.idAttrs
    },
    reviews: CONTROL_MAP.reviews,
    atc: CONTROL_MAP.atc + ', ' + TREATMENT_CTA_SEL,
    atcVisible: CONTROL_MAP.atcVisible + ', ' + TREATMENT_CTA_SEL
  };

  function selectorMap(doc) {
    try {
      if (doc && doc.querySelector && doc.querySelector(TREATMENT_HOST_SEL)) return TREATMENT_MAP;
    } catch (e) { /* exotic selector support — fall back to control */ }
    return CONTROL_MAP;
  }

  /* Every observable slide in ONE gallery root, in DOM order. Scoping the
     query to the root is what excludes thumbnails — on this theme the strip
     (.product_gallery_nav) is a SIBLING of the gallery, not a child. The
     next-image peek needs no rule of its own: the caller's 50% threshold
     cannot be met by a sliver of the neighbouring slide. */
  function galleryMedia(root, itemSel, idAttrs) {
    var items;
    try { items = (root && root.querySelectorAll) ? [].slice.call(root.querySelectorAll(itemSel)) : []; }
    catch (e) { return []; }
    return items.map(function (el, i) {
      var d = describeMedia(el, idAttrs);
      return {
        el: el, mediaEl: d.el,
        media_id: d.media_id, media_type: d.media_type,
        position: i + 1, gallery_size: items.length
      };
    });
  }

  /* brief section 3: placement of a reviews link — an explicit
     data-dw-placement anywhere above it, else the nearest ancestor id. */
  function placementOf(el) {
    try {
      var tagged = el && el.closest ? el.closest('[data-dw-placement]') : null;
      if (tagged) return attrOf(tagged, 'data-dw-placement');
      var ided = el && el.closest ? el.closest('[id]') : null;
      return (ided && ided.id) || null;
    } catch (e) { return null; }
  }

  /* pdp_video_progress "half": guards a live stream (duration Infinity) and a
     video whose metadata has not loaded (duration NaN / 0). */
  function halfWatched(currentTime, duration) {
    var d = Number(duration);
    if (!isFinite(d) || d <= 0) return false;
    return (Number(currentTime) / d) >= 0.5;
  }

  /* --- visibility gate (fix round 1, TAU-358) -----------------------------
     "≥50% visible for one continuous second while the tab is visible" — the
     rule behind pdp_media_view / pdp_reviews_view / pdp_atc_visible. Pulled
     out of the snippet's makeWatcher so it is unit-testable: an
     IntersectionObserver only fires on a threshold CROSSING, so a node that
     was already ≥50% before the tab was hidden gets no new entry when the
     tab comes back — the old inline version never re-armed for it. This
     version keeps the last known ratio per node and re-evaluates it on
     visible(), which is the actual fix.

     A hide always cancels the pending timer; a later visible() restarts the
     1s count from zero for any node still ≥50% (never resumes a partial
     count) — same "continuous one second" semantics as before. */
  function createVisibilityGate(opts) {
    opts = opts || {};
    var now = opts.now || function () { return Date.now(); };
    var scheduleTimeout = opts.setTimeout || setTimeout;
    var cancelTimeout = opts.clearTimeout || clearTimeout;
    var onQualified = opts.onQualified || function () {};
    var DURATION_MS = 1000;

    var entries = [];   /* { node, ratio, timer, done } */
    var tabVisible = true;

    function find(node) {
      for (var i = 0; i < entries.length; i++) if (entries[i].node === node) return entries[i];
      return null;
    }

    function forget(entry) {
      var i = entries.indexOf(entry);
      if (i !== -1) entries.splice(i, 1); /* Minor 1: drop it once it is done */
    }

    function cancel(entry) {
      if (entry.timer) { cancelTimeout(entry.timer); entry.timer = null; }
    }

    function arm(entry) {
      if (entry.timer || entry.done) return;
      entry.timer = scheduleTimeout(function () {
        entry.timer = null;
        entry.done = true;
        forget(entry);
        onQualified(entry.node);
      }, DURATION_MS);
    }

    return {
      /* Called from the IntersectionObserver callback with the node's
         current ratio (0 when not intersecting at all). */
      update: function (node, ratio) {
        var entry = find(node);
        if (!entry) {
          entry = { node: node, ratio: 0, timer: null, done: false };
          entries.push(entry);
        }
        if (entry.done) return;
        entry.ratio = ratio;
        if (ratio >= 0.5) { if (tabVisible) arm(entry); }
        else cancel(entry);
      },
      /* Tab went hidden: cancel every pending timer, but remember each
         node's last ratio so visible() can re-arm the qualifying ones. */
      hidden: function () {
        tabVisible = false;
        entries.forEach(cancel);
      },
      /* Tab came back: the fix — re-evaluate every watched node's CURRENT
         (last known) ratio and restart the 1s timer for anything still
         >=50%, since no new IntersectionObserver entry is coming for a node
         whose ratio never changed across the hide. */
      visible: function () {
        tabVisible = true;
        entries.forEach(function (entry) {
          if (!entry.done && entry.ratio >= 0.5) arm(entry);
        });
      },
      /* The caller unobserved/removed a node (e.g. a stale gallery slide) —
         drop its bookkeeping too. */
      remove: function (node) {
        var entry = find(node);
        if (entry) { cancel(entry); forget(entry); }
      }
    };
  }

  /* --- network hooks -------------------------------------------------------
     Wraps win.fetch and win.XMLHttpRequest so every add path (product-form
     buyNow, the drawer upsell, app.js's jQuery XHR) is observed by ONE code
     path. Lives here rather than inline in the snippet so qa/dw-events.test.mjs
     can drive it with a fake window ({ fetch, XMLHttpRequest }); the snippet
     just calls installHooks(window, tracker).

     Contract: the host page always gets its own promise and its own response
     back untouched, and nothing in here throws into it — the wrapper's own
     work is inside try/catch and every promise callback inside guard().
     ---------------------------------------------------------------------- */
  function installHooks(win, tracker) {
    /* clone first — the host page still needs to read its own body. Returns
       null (never throws) if the body has somehow already been used. */
    function bodyOf(res) {
      try { return res.clone().json(); } catch (err) { return null; }
    }

    /* A throw inside one of these callbacks would reject a promise nobody
       handles; this hook must be invisible to the page, console included. */
    function guard(fn) {
      return function (arg) {
        try { fn(arg); } catch (err) { /* fail-silent */ }
      };
    }

    var origFetch = (typeof win.fetch === 'function') ? win.fetch : null;
    if (origFetch) {
      win.fetch = function (input) {
        var url = '';
        try { url = (input && typeof input === 'object' && input.url) ? input.url : String(input); } catch (err) { url = ''; }
        /* TAU-360: write the attribution INTO the add request. Shopify applies
           attributes[...] fields on /cart/add.js atomically, so the order can
           no longer be unattributable when a fast add -> checkout outruns the
           separate /cart/update.js. `arguments[1] && .body` on purpose: a
           Request input carries its body out of reach, and that path keeps the
           post-add syncCartAttributes fallback. */
        try {
          var init = arguments[1];
          if (isAddUrl(url) && init && init.body && tracker.applyAttribution) {
            var applied = tracker.applyAttribution(init.body);
            if (typeof applied === 'string') init.body = applied; /* JSON / urlencoded string bodies come back rewritten */
          }
        } catch (err) { /* fail-silent — the request goes out untouched */ }
        var p = origFetch.apply(win, arguments);
        try {
          if (isAddUrl(url)) {
            p.then(guard(function (res) {
              var body = bodyOf(res);
              if (!body) { tracker.addResponse(res.status, null); return; }
              body.then(
                guard(function (json) { tracker.addResponse(res.status, json); }),
                guard(function () { tracker.addResponse(res.status, null); })
              );
            }), guard(function () { tracker.addResponse(null, null); }));
          } else if (isCartRefreshUrl(url)) {
            p.then(guard(function (res) {
              if (!res.ok) { tracker.cartRefreshFailure('http_' + res.status); return; }
              var body = bodyOf(res);
              if (!body) return;
              body.then(
                guard(function (json) { tracker.updateCart(json); }),
                guard(function () { /* the drawer section render is HTML, not JSON */ })
              );
            }), guard(function () { tracker.cartRefreshFailure('network'); }));
          }
        } catch (err) { /* fail-silent */ }
        return p;
      };
    }

    var XHR = win.XMLHttpRequest;
    if (XHR && XHR.prototype && XHR.prototype.open && XHR.prototype.send) {
      var origOpen = XHR.prototype.open;
      var origSend = XHR.prototype.send;

      XHR.prototype.open = function (method, url) {
        try { this.__dwUrl = url; } catch (err) { /* frozen instance */ }
        return origOpen.apply(this, arguments);
      };

      XHR.prototype.send = function (body) {
        try {
          var xhr = this;
          var isAdd = isAddUrl(xhr.__dwUrl);
          var isRefresh = !isAdd && isCartRefreshUrl(xhr.__dwUrl);
          /* same atomic write as the fetch path above (app.js adds over XHR) */
          if (isAdd && body && tracker.applyAttribution) {
            var appliedX = tracker.applyAttribution(body);
            if (typeof appliedX === 'string') arguments[0] = appliedX; /* strict mode: arguments is unlinked from `body`, so set it explicitly for the apply() below */
          }
          if (isAdd || isRefresh) {
            xhr.addEventListener('load', function () {
              try {
                var body = null;
                try { body = JSON.parse(xhr.responseText); }
                catch (err) { body = (xhr.response && typeof xhr.response === 'object') ? xhr.response : null; }
                if (isAdd) { tracker.addResponse(xhr.status, body); return; }
                if (xhr.status < 200 || xhr.status >= 300) tracker.cartRefreshFailure('http_' + xhr.status);
                else tracker.updateCart(body);
              } catch (err) { /* fail-silent */ }
            });
            xhr.addEventListener('error', function () {
              try {
                if (isAdd) tracker.addResponse(null, null);
                else tracker.cartRefreshFailure('network');
              } catch (err) { /* fail-silent */ }
            });
          }
        } catch (err) { /* fail-silent */ }
        return origSend.apply(this, arguments);
      };
    }
  }

  /* --- tracker -------------------------------------------------------------
     Everything that DECIDES what to push lives here, behind injected
     dependencies, so qa/dw-events.test.mjs can drive the whole event surface
     with plain objects. snippets/dw-events.liquid supplies the real
     dependencies and does nothing but call these methods from DOM listeners
     and the fetch/XHR hooks.

     env = {
       dataLayer,                         // array-like with .push
       now(),                             // ms clock (injectable for tests)
       uuid(),                            // event_id / attempt_id source
       currency,                          // shop currency, from Liquid
       productId, productHandle, variantId, // page context, from Liquid
       cart,                              // /cart.js-shaped, seeded from Liquid
       cartAttrs,                         // { dw_vid, dw_exp } already on the cart
       exp(),                             // () => window.dwExp
       vid(), pvid(),                     // fallbacks when dwExp carries neither
       fetch(url, init)                   // the ORIGINAL fetch, never the wrapped one
     }
  ------------------------------------------------------------------------- */
  function createTracker(env) {
    var dataLayer = env.dataLayer;
    var now = env.now || function () { return Date.now(); };
    var uuid = env.uuid || uuid4;

    var clickState = {};      /* ATC clicks — anchors attempt_id */
    var checkoutState = {};   /* checkout clicks — dedupe only */
    var variantState = { variantId: env.variantId || null };
    var cart = env.cart || null;
    var cartAttrs = env.cartAttrs || null;
    var lastAdd = null;
    var seenVisible = {};
    var seenMedia = {};       /* media_id -> pdp_media_view already pushed */
    var seenMilestones = {};  /* media_id|milestone -> pdp_video_progress already pushed */
    var seenReviews = false;

    function rawExp() {
      try { return env.exp ? env.exp() : null; } catch (err) { return null; }
    }

    /* null unless the visitor is actually enrolled (dwExp.arm is null for
       everyone the assignment snippet did not draw in, and dwExp itself is
       absent when the experiment setting is off). */
    function enrolled() {
      var e = rawExp();
      return (e && e.arm) ? e : null;
    }

    function fallback(fn) {
      try { return fn ? fn() : null; } catch (err) { return null; }
    }

    function envelope(event, extra) {
      var e = rawExp();
      var p = {
        event: event,
        event_id: uuid(),
        ts: new Date(now()).toISOString(),
        /* Global Constraints: dw_vid and page_view_id ride EVERY push, enrolled
           or not — only the experiment_* pair is gated on enrolment. dwExp
           carries neither for a visitor the assignment snippet bailed on, hence
           the fallbacks (localStorage.dw_vid / window.__dwPvid). */
        dw_vid: (e && e.vid) || fallback(env.vid) || null,
        page_view_id: (e && e.pvid) || fallback(env.pvid) || null,
        product_id: env.productId || null,
        product_handle: env.productHandle || null,
        variant_id: variantState.variantId || null
      };
      if (e && e.arm) {
        p.experiment_id = EXPERIMENT_NAME_KEY;
        p.experiment_variant = e.arm;
      }
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) p[k] = extra[k];
      }
      return p;
    }

    /* CONSENT SEAM (WP-6.1): gate here on analytics_storage — every non-ecom
       tracker event funnels through this one function. */
    function push(event, extra) {
      var p = envelope(event, extra);
      dataLayer.push(p);
      return p;
    }

    /* GA4 hygiene, same convention as snippets/cro-events.liquid: clear the
       previous ecommerce object before pushing a new one. */
    /* CONSENT SEAM (WP-6.1): gate here on analytics_storage — the ecommerce
       counterpart of push(); add_to_cart / begin_checkout come through here. */
    function pushEcom(event, ecommerce, extra) {
      dataLayer.push({ ecommerce: null });
      var p = envelope(event, extra);
      p.ecommerce = ecommerce;
      dataLayer.push(p);
      return p;
    }

    var tracker = {
      /* An add-to-cart CTA was clicked. Returns the attempt (or null when the
         click was deduped) so the caller can correlate if it wants to. */
      atcClick: function (target) {
        var attempt = recordClick(clickState, target, now(), uuid());
        if (!attempt) return null;
        push('pdp_atc_click', {
          attempt_id: attempt.id,
          location: locationOf(target),
          in_stock: !(target && target.disabled)
        });
        return attempt;
      },

      /* A /cart/add(.js) request finished. `status` is null for a thrown or
         aborted request; `body` is the parsed JSON (or null). */
      addResponse: function (status, body) {
        var ok = typeof status === 'number' && status >= 200 && status < 300;
        var parsed = ok ? itemsFromAddResponse(body) : null;
        var attempt = takeAttempt(clickState, now());
        var attemptId = attempt ? attempt.id : null;

        if (!ok) {
          return push('pdp_atc_error', {
            attempt_id: attemptId,
            error_category: errorCategory(status, body && body.description)
          });
        }

        /* A 2xx whose body will not parse as a line item is still an ADD —
           brief §2 makes errors non-2xx-or-throw, nothing else. Push
           add_to_cart with the fields the page already knows (product/variant
           from the form) rather than inventing a failure the shopper never
           saw. quantity/value stay null: unknown, not zero. */
        if (!parsed) {
          parsed = { items: [], quantity: null, value: null, product_id: null, product_handle: null, variant_id: null };
        }

        lastAdd = { attempt_id: attemptId, at: now() };
        var p = pushEcom(
          'dw_add_to_cart', /* NOT 'add_to_cart': the Shopify custom pixel (via pixel-bridge.liquid) already pushes GA4's add_to_cart; this one carries the experiment envelope for DataWorks (tracking definition trigger = dw_add_to_cart, event = add_to_cart) */
          { currency: env.currency || undefined, value: parsed.value, items: parsed.items },
          {
            location: attempt ? locationOf(attempt.target) : 'unknown',
            quantity: parsed.quantity,
            attempt_id: attemptId,
            value: parsed.value,
            product_id: parsed.product_id || env.productId || null,
            product_handle: parsed.product_handle || env.productHandle || null,
            variant_id: parsed.variant_id || variantState.variantId || null
          }
        );
        tracker.syncCartAttributes();
        return p;
      },

      /* The theme's drawer refresh (/cart.js or ?section_id=cart-drawer)
         failed. Only meaningful straight after an add — otherwise it is just
         a cart page having a bad day, which is not this experiment's problem. */
      cartRefreshFailure: function (category) {
        if (!lastAdd || (now() - lastAdd.at) > ATTEMPT_MAX_AGE_MS) return null;
        return push('cart_drawer_error', {
          attempt_id: lastAdd.attempt_id,
          error_category: category
        });
      },

      /* Keep begin_checkout's value/items off the (possibly stale) server
         render by absorbing every /cart.js body the page fetches. */
      updateCart: function (cartJson) {
        if (cartJson && typeof cartJson === 'object') cart = cartJson;
      },

      checkoutClick: function (target, isDynamic) {
        if (!recordClick(checkoutState, target, now(), uuid())) return null;
        /* Last chance before the page navigates away: re-assert the attribution
           (keepalive) for any cart the add path could not write it into. */
        tracker.syncCartAttributes();
        var ecommerce = cartToEcommerce(cart, env.currency);
        return pushEcom('begin_checkout', ecommerce, {
          location: isDynamic ? 'dynamic' : 'standard',
          value: ecommerce.value
        });
      },

      variantChange: function (newVariantId, option) {
        var payload = variantSelect(variantState, newVariantId, option);
        if (!payload) return null;
        return push('pdp_variant_select', payload);
      },

      /* Experiment-only (brief section 1): a brand new event with no prior
         producer, so there is no non-enrolled coverage to preserve. */
      atcVisible: function (location) {
        if (!enrolled()) return null;
        if (seenVisible[location]) return null;
        seenVisible[location] = true;
        return push('pdp_atc_visible', { location: location });
      },

      /* ---- TAU-358 gallery / video / reviews diagnostics -----------------
         All four are EXPERIMENT diagnostics, not GA4 events: they push only
         for an enrolled visitor (brief section 1). `info` comes from
         galleryMedia() above, so position/gallery_size are always the state of
         the gallery AS OBSERVED — a colour change that replaces the gallery
         re-resolves them. Dedupe is on media_id, never on the element, which
         is what makes image 1 -> 2 -> 1 two views and not three. */
      mediaView: function (info) {
        if (!enrolled()) return null;
        if (!info || !info.media_id) return null;
        if (seenMedia[info.media_id]) return null;
        seenMedia[info.media_id] = true;
        return push('pdp_media_view', {
          media_id: info.media_id,
          media_type: info.media_type || 'image',
          position: info.position || null,
          gallery_size: info.gallery_size || null
        });
      },

      /* milestone is 'start' | 'half' | 'complete', once each per video. */
      videoProgress: function (mediaId, milestone) {
        if (!enrolled()) return null;
        if (!mediaId || !milestone) return null;
        var k = mediaId + '|' + milestone;
        if (seenMilestones[k]) return null;
        seenMilestones[k] = true;
        return push('pdp_video_progress', { media_id: mediaId, milestone: milestone });
      },

      reviewsClick: function (placement) {
        if (!enrolled()) return null;
        return push('pdp_reviews_click', { placement: placement || null });
      },

      /* "at least the first loaded review card", once per page view. */
      reviewsView: function () {
        if (!enrolled()) return null;
        if (seenReviews) return null;
        seenReviews = true;
        return push('pdp_reviews_view', {});
      },

      /* The cart attributes shaped as /cart/add.js form field names, or null
         for a visitor who is not enrolled. Same values as cartAttributes(). */
      attributionFields: function () {
        var e = enrolled();
        if (!e || !e.vid) return null;
        var a = cartAttributes(e.vid, e.arm, new Date(now()).toISOString());
        return {
          'attributes[dw_vid]': a.dw_vid,
          'attributes[dw_exp]': a.dw_exp,
          'attributes[dw_exp_ts]': a.dw_exp_ts
        };
      },

      /* Append those fields onto an outgoing add body so Shopify sets the cart
         attributes in the SAME request as the add (verified on the live store:
         attributes[dw_probe] on /cart/add.js showed up in /cart.js). Only a
         FormData or URLSearchParams — duck-typed on has/append — is touched; a
         JSON string body is left alone and the post-add syncCartAttributes
         still covers it. Never throws: on any failure the request goes out
         exactly as the page built it. */
      applyAttribution: function (bodyIn) {
        try {
          var fields = tracker.attributionFields();
          if (!fields || !bodyIn) return null;
          /* String bodies (11 Sep, order #10508 went out unattributed): the
             theme's buyNow() posts JSON and redirects to /checkout in the same
             tick, and app.js adds over jQuery XHR with a urlencoded string —
             both too fast for the post-add /cart/update.js. Shopify accepts an
             `attributes` object in a JSON add body and attributes[...] pairs in
             a urlencoded one (both verified on the live store), so rewrite the
             string and hand it back for the caller to send. */
          if (typeof bodyIn === 'string') {
            var trimmed = bodyIn.replace(/^\s+/, '');
            if (trimmed.charAt(0) === '{') {
              var obj = JSON.parse(trimmed);
              if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
              var attrs = (obj.attributes && typeof obj.attributes === 'object') ? obj.attributes : {};
              for (var jk in fields) {
                if (Object.prototype.hasOwnProperty.call(fields, jk)) {
                  var bare = jk.slice('attributes['.length, -1);
                  if (!(bare in attrs)) attrs[bare] = fields[jk];
                }
              }
              obj.attributes = attrs;
              cartAttrs = { dw_vid: fields['attributes[dw_vid]'], dw_exp: fields['attributes[dw_exp]'] };
              return JSON.stringify(obj);
            }
            if (bodyIn.indexOf('=') === -1) return null; /* not a form body we understand */
            var out = bodyIn;
            for (var uk in fields) {
              if (Object.prototype.hasOwnProperty.call(fields, uk) && out.indexOf(encodeURIComponent(uk) + '=') === -1 && out.indexOf(uk + '=') === -1) {
                out += (out.length ? '&' : '') + encodeURIComponent(uk) + '=' + encodeURIComponent(fields[uk]);
              }
            }
            cartAttrs = { dw_vid: fields['attributes[dw_vid]'], dw_exp: fields['attributes[dw_exp]'] };
            return out;
          }
          if (typeof bodyIn.append !== 'function' || typeof bodyIn.has !== 'function') return null;
          for (var k in fields) {
            if (Object.prototype.hasOwnProperty.call(fields, k) && !bodyIn.has(k)) bodyIn.append(k, fields[k]);
          }
          /* the cart will carry them the moment this add lands — stop the
             post-add sync from issuing a redundant /cart/update.js */
          cartAttrs = { dw_vid: fields['attributes[dw_vid]'], dw_exp: fields['attributes[dw_exp]'] };
          return fields;
        } catch (err) { return null; }
      },

      /* TAU-360. Fire-and-forget: never awaited, never blocks the UI, and
         uses the ORIGINAL fetch so it cannot re-enter the ATC hook. */
      /* CONSENT SEAM (WP-6.1): gate here on analytics_storage — this writes
         dw_vid / dw_exp onto the Shopify cart, so it is the one seam that
         persists an identifier off-page rather than only pushing to GTM. */
      syncCartAttributes: function () {
        var e = enrolled();
        if (!e || !e.vid) return null;
        var desired = cartAttributes(e.vid, e.arm, new Date(now()).toISOString());
        if (!attributesDiffer(cartAttrs, desired)) return null;
        /* Record the intent BEFORE the request so a slow or failing write
           cannot produce a second one on the next add. */
        cartAttrs = { dw_vid: desired.dw_vid, dw_exp: desired.dw_exp };
        try {
          var r = env.fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: desired }),
            /* the checkout click fires this immediately before navigating —
               without keepalive the browser cancels it in flight */
            keepalive: true
          });
          if (r && typeof r.catch === 'function') r.catch(function () { /* silent */ });
        } catch (err) { /* silent — attribution is never worth an exception */ }
        return desired;
      }
    };

    return tracker;
  }

  var core = {
    EXPERIMENT_NAME_KEY: EXPERIMENT_NAME_KEY,
    ATTEMPT_MAX_AGE_MS: ATTEMPT_MAX_AGE_MS,
    CLICK_DEDUPE_MS: CLICK_DEDUPE_MS,
    uuid4: uuid4,
    isAddUrl: isAddUrl,
    isCartRefreshUrl: isCartRefreshUrl,
    recordClick: recordClick,
    takeAttempt: takeAttempt,
    locationOf: locationOf,
    errorCategory: errorCategory,
    itemsFromAddResponse: itemsFromAddResponse,
    cartToEcommerce: cartToEcommerce,
    cartAttributes: cartAttributes,
    attributesDiffer: attributesDiffer,
    variantSelect: variantSelect,
    mediaIdFromSrc: mediaIdFromSrc,
    describeMedia: describeMedia,
    galleryMedia: galleryMedia,
    selectorMap: selectorMap,
    TREATMENT_HOST_SEL: TREATMENT_HOST_SEL,
    firstMatch: firstMatch,
    placementOf: placementOf,
    halfWatched: halfWatched,
    createVisibilityGate: createVisibilityGate,
    createTracker: createTracker,
    installHooks: installHooks
  };

  if (typeof window !== 'undefined') window.__dwEventsCore = core;
  if (typeof module !== 'undefined') module.exports = core;
})();
